// src/components/plots/EnrichmentPlots.tsx
import React, { useEffect, useMemo } from "react";
import BubblePlot from "./BubblePlot";

export interface EnrichmentPlotsProps {
  data: any;
  onClusterRowsChange?: (rows: any[]) => void;
  // NEW: controlled selection from parent
  selectedCluster?: string | null;
  onSelectedClusterChange?: (id: string | null) => void;
}

/**
 * data can be:
 * - non-precomputed: array of rows
 * - precomputed: {cluster_0:{plot,enriched}, ...}
 * - precomputed wrapped: [ {cluster_0:{...}, ...} ]
 */
const EnrichmentPlots: React.FC<EnrichmentPlotsProps> = ({
  data,
  onClusterRowsChange,
  selectedCluster,
  onSelectedClusterChange,
}) => {
  // 🔍 Normalize input
  const { precomputedContainer, plainRows } = useMemo(() => {
    let precomputedContainer: any = null;
    let plainRows: any[] = [];

    if (Array.isArray(data)) {
      // Case: [ { cluster_0: {...}, cluster_1: {...}, ... } ]
      if (
        data.length === 1 &&
        data[0] &&
        typeof data[0] === "object" &&
        Object.keys(data[0]).some((k) => k.startsWith("cluster_"))
      ) {
        precomputedContainer = data[0];
      } else {
        // Already rows
        plainRows = data;
      }
    } else if (data && typeof data === "object") {
      // Case: { cluster_0: {...}, cluster_1: {...}, ... }
      if (Object.keys(data).some((k) => k.startsWith("cluster_"))) {
        precomputedContainer = data;
      }
    }

    return { precomputedContainer, plainRows };
  }, [data]);

  const isPrecomputed = !!precomputedContainer;

  const clusterIds: string[] = useMemo(() => {
    if (!isPrecomputed || !precomputedContainer) return [];
    return Object.keys(precomputedContainer).filter((k) =>
      k.startsWith("cluster_"),
    );
  }, [isPrecomputed, precomputedContainer]);

  /**
   * Ensure the selected cluster is valid:
   * - If precomputed and there are clusters but selectedCluster is null/invalid → default to first cluster.
   * - This lives entirely in parent via onSelectedClusterChange (no local state).
   */
  useEffect(() => {
    if (!isPrecomputed) return;
    if (!clusterIds.length) return;

    if (!selectedCluster || !clusterIds.includes(selectedCluster)) {
      const first = clusterIds[0];
      onSelectedClusterChange?.(first);
    }
  }, [isPrecomputed, clusterIds, selectedCluster, onSelectedClusterChange]);

  const effectiveSelectedCluster =
    isPrecomputed && clusterIds.length
      ? (selectedCluster && clusterIds.includes(selectedCluster)
          ? selectedCluster
          : clusterIds[0])
      : null;

  // 👉 Compute rows fed into BubblePlot AND table
  const rows: any[] = useMemo(() => {
    // Non-precomputed → already row array
    if (!isPrecomputed) return plainRows;

    if (!effectiveSelectedCluster || !precomputedContainer) return [];

    const clusterObj = precomputedContainer[effectiveSelectedCluster];

    if (!clusterObj?.enriched) {
      console.warn(
        "[EnrichmentPlots] No 'enriched' field for",
        effectiveSelectedCluster,
        clusterObj,
      );
      return [];
    }

    try {
      const parsed = JSON.parse(clusterObj.enriched);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      console.warn(
        "[EnrichmentPlots] Parsed 'enriched' is not an array for",
        effectiveSelectedCluster,
      );
      return [];
    } catch (e) {
      console.error(
        "[EnrichmentPlots] Failed to parse 'enriched' for",
        effectiveSelectedCluster,
        e,
      );
      return [];
    }
  }, [isPrecomputed, plainRows, effectiveSelectedCluster, precomputedContainer]);

  // 🔼 Push current rows up to Results so it can feed ResultsTable
  useEffect(() => {
    onClusterRowsChange?.(rows);
  }, [rows, onClusterRowsChange]);

  // Helpful debug: check columns of first row
  if (rows.length > 0) {
    console.log("[EnrichmentPlots] first row keys:", Object.keys(rows[0]));
  }

  // Non-precomputed: no cluster selector
  if (!isPrecomputed) {
    return (
      <div style={{ width: "100%", height: "100%" }}>
        <BubblePlot data={rows} />
      </div>
    );
  }

  // Precomputed but no clusters
  if (!clusterIds.length) {
    return <p>No enrichment data available.</p>;
  }

  const handleClusterChange = (value: string) => {
    onSelectedClusterChange?.(value);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {/* Cluster dropdown */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ marginRight: 8 }}>Cluster:</label>
        <select
          value={effectiveSelectedCluster || ""}
          onChange={(e) => handleClusterChange(e.target.value)}
        >
          {clusterIds.map((cl) => (
            <option key={cl} value={cl}>
              {cl.replace("cluster_", "Cluster ")}
            </option>
          ))}
        </select>
      </div>

      {/* Scrollable bubble plot */}
      <div
            style={{
                flex: 1,        // take all available space
                minHeight: 0,   // allow shrinking in flex layouts
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "8px",
            }}
            >
        <BubblePlot data={rows} />
      </div>
    </div>
  );
};

export default EnrichmentPlots;